import React, { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getAssetCategories,
  SCreateAssetCategory,
  SUpdateAssetCategory,
  SDeleteAssetCategory,
} from "../../../apis/app.service";
import AppButton from "../../sharedcomponents/Buttons/AppButton/AppButton";
import { IAssetCategory } from "../core/models";

export default function ManageCategories() {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newPrefix, setNewPrefix] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editPrefix, setEditPrefix] = useState("");

  const { data: categories, isLoading } = useQuery<IAssetCategory[]>({
    queryKey: ["asset-categories"],
    queryFn: getAssetCategories,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["asset-categories"] });
    queryClient.invalidateQueries({ queryKey: ["assets"] });
  };

  const createMutation = useMutation({
    mutationFn: SCreateAssetCategory,
    onSuccess: () => {
      invalidate();
      toast.success("Category added.", { position: "top-right" });
      setNewName("");
      setNewPrefix("");
    },
    onError: (err: Error) =>
      toast.error(err.message, { position: "top-right" }),
  });

  const updateMutation = useMutation({
    mutationFn: SUpdateAssetCategory,
    onSuccess: () => {
      invalidate();
      toast.success("Category updated.", { position: "top-right" });
      setEditingId(null);
    },
    onError: (err: Error) =>
      toast.error(err.message, { position: "top-right" }),
  });

  const deleteMutation = useMutation({
    mutationFn: SDeleteAssetCategory,
    onSuccess: () => {
      invalidate();
      toast.success("Category deleted.", { position: "top-right" });
    },
    onError: (err: Error) =>
      toast.error(err.message, { position: "top-right" }),
  });

  const startEdit = (cat: IAssetCategory) => {
    setEditingId(cat._id);
    setEditName(cat.name);
    setEditPrefix(cat.prefix || "");
  };

  const saveEdit = (id: string) => {
    if (!editName.trim()) return;
    updateMutation.mutate({
      categoryId: id,
      body: { name: editName.trim(), prefix: editPrefix.trim() },
    });
  };

  const handleAdd = () => {
    if (!newName.trim()) return;
    createMutation.mutate({
      name: newName.trim(),
      prefix: newPrefix.trim() || undefined,
    });
  };

  return (
    <div className="modal fade" id="kt_modal_manage_categories" aria-hidden="true">
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "560px" }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="asset-modal-title">Manage Categories</h2>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body">
            <div className="row g-2 mb-3 align-items-center">
              <div className="col-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="New category name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
              </div>
              <div className="col-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Prefix"
                  maxLength={5}
                  value={newPrefix}
                  onChange={(e) => setNewPrefix(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
              </div>
              <div className="col-3">
                <AppButton
                  btnType="success"
                  width="100%"
                  disabled={createMutation.isPending || !newName.trim()}
                  onClick={handleAdd}
                >
                  Add
                </AppButton>
              </div>
            </div>

            {isLoading ? (
              <p className="text-muted">Loading…</p>
            ) : (
              <ul className="asset-category-list">
                {categories?.map((cat) => (
                  <li key={cat._id} className="asset-category-row">
                    {editingId === cat._id ? (
                      <>
                        <input
                          type="text"
                          className="form-control form-control-sm asset-category-name-input"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          autoFocus
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm asset-category-prefix-input"
                          maxLength={5}
                          value={editPrefix}
                          onChange={(e) =>
                            setEditPrefix(e.target.value.toUpperCase())
                          }
                        />
                        <span
                          className="asset-icon-btn"
                          title="Save"
                          onClick={() => saveEdit(cat._id)}
                        >
                          <i className="fas fa-check"></i>
                        </span>
                        <span
                          className="asset-icon-btn"
                          title="Cancel"
                          onClick={() => setEditingId(null)}
                        >
                          <i className="fas fa-times"></i>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="asset-category-name">{cat.name}</span>
                        <span className="asset-category-prefix">{cat.prefix}</span>
                        <span className="asset-category-count">
                          {cat.count ?? 0} asset{cat.count === 1 ? "" : "s"}
                        </span>
                        <span
                          className="asset-icon-btn"
                          title="Rename"
                          onClick={() => startEdit(cat)}
                        >
                          <i className="fas fa-edit"></i>
                        </span>
                        <span
                          className={`asset-icon-btn asset-icon-btn--danger ${
                            cat.count ? "asset-icon-btn--disabled" : ""
                          }`}
                          title={
                            cat.count
                              ? `Reassign ${cat.count} asset(s) before deleting`
                              : "Delete"
                          }
                          onClick={() => {
                            if (!cat.count) deleteMutation.mutate(cat._id);
                          }}
                        >
                          <i className="fas fa-trash"></i>
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
